import { fieldIdentity } from "@/lib/itsm-config/field-mapping";
import type {
  MondayBoardSync,
  MondayHarmonyField,
  MondaySyncDirection,
  ProviderConfig,
} from "@/lib/types/itsm-config";

/**
 * The monday boards two-way sync can touch: every board an export rule routes
 * to, plus any board that already has columns configured (its rule may have
 * been removed while items created from it still sync).
 */
export function mondayBoards(config: ProviderConfig): string[] {
  const fromRules = (config.export_mapping ?? [])
    .map((r) => r.provider_scope)
    .filter((b): b is string => Boolean(b));
  return [...new Set([...fromRules, ...Object.keys(config.board_sync ?? {})])];
}

/** Boards whose items are imported as tickets, each with the desk they land
 *  in. Mirrors the backend's `monday_import_boards`: a board with import on
 *  but no desk is staged, not live. */
export function mondayImportBoards(
  config: ProviderConfig,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(config.board_sync ?? {}).flatMap(([boardId, sync]) =>
      sync.import_enabled && sync.import_desk_id
        ? [[boardId, sync.import_desk_id]]
        : [],
    ),
  );
}

/** The draft with one board's sync settings patched. Everything else on the
 *  board and in the config is kept as stored: the config is saved whole, so a
 *  field this editor does not know about must survive the round trip. */
export function patchBoardSync(
  config: ProviderConfig,
  boardId: string,
  patch: Partial<MondayBoardSync>,
): Pick<ProviderConfig, "board_sync"> {
  const all = config.board_sync ?? {};
  return {
    board_sync: {
      ...all,
      // eslint-disable-next-line security/detect-object-injection -- boardId is a monday board id from our own config
      [boardId]: { ...all[boardId], ...patch },
    },
  };
}

/** A board's custom field mapping and its create-only list, written together
 *  so the list never names a field the board no longer maps. Empty values are
 *  saved as null, as the backend defaults them. */
export function customFieldMappingPatch(
  columns: Record<string, string>,
  createOnly: readonly string[],
): Pick<MondayBoardSync, "custom_field_columns" | "create_only_custom_fields"> {
  const kept = createOnly.filter((key) => key in columns);
  return {
    custom_field_columns: Object.keys(columns).length ? columns : null,
    create_only_custom_fields: kept.length ? kept : null,
  };
}

/** The target that is the item's own name rather than a column. */
export const ITEM_NAME_TARGET = "name";

export const MONDAY_BUILTIN_FIELDS = [
  "title",
  "description",
  "priority",
  "assignee",
  "reporter",
  "status",
] as const;

/** Built-ins that may take the item name - the text ones. */
export const NAME_TARGET_BUILTINS = new Set(["title", "description"]);

export type PairingSource = "explicit" | "legacy" | "default";

/** How a pairing syncs: a direction, or only when the item is created. */
export type PairingSync = MondaySyncDirection | "create";

export interface MondayPairing {
  harmony: MondayHarmonyField;
  target: string;
  direction: PairingSync;
  source: PairingSource;
  /** Index into `field_mappings`, for explicit pairings. */
  index?: number;
}

export { fieldIdentity };

/**
 * The board's effective pairings, as the backend reads them
 * (`itsm_integration_core.monday_field_mapping`): explicit entries, then the
 * legacy slots, then the default title -> item name. The first claim of a
 * Harmony field or a target wins, so an explicit pairing displaces a default.
 */
export function boardPairings(sync: MondayBoardSync | undefined): {
  pairings: MondayPairing[];
  usedFields: Set<string>;
  usedTargets: Set<string>;
} {
  const usedFields = new Set<string>();
  const usedTargets = new Set<string>();
  const pairings: MondayPairing[] = [];
  if (sync?.files_column_id) usedTargets.add(sync.files_column_id);

  const claim = (pairing: MondayPairing) => {
    const id = fieldIdentity(pairing.harmony);
    if (usedFields.has(id) || usedTargets.has(pairing.target)) return;
    usedFields.add(id);
    usedTargets.add(pairing.target);
    pairings.push(pairing);
  };

  const createOnly = new Set(sync?.create_only_custom_fields ?? []);
  const settled = (p: MondayPairing): MondayPairing =>
    p.harmony.kind === "custom" && createOnly.has(p.harmony.key)
      ? { ...p, direction: "create" }
      : p;
  (sync?.field_mappings ?? []).forEach((m, index) => {
    claim(
      settled({
        harmony: m.harmony,
        target: m.target,
        direction: m.direction ?? "both",
        source: "explicit",
        index,
      }),
    );
  });
  if (sync?.status_column_id) {
    claim({
      harmony: { kind: "builtin", key: "status" },
      target: sync.status_column_id,
      direction: "both",
      source: "legacy",
    });
  }
  if (sync?.assignee_column_id) {
    claim({
      harmony: { kind: "builtin", key: "assignee" },
      target: sync.assignee_column_id,
      direction: "inbound",
      source: "legacy",
    });
  }
  for (const [key, column] of Object.entries(
    sync?.custom_field_columns ?? {},
  )) {
    claim(
      settled({
        harmony: { kind: "custom", key },
        target: column,
        direction: "outbound",
        source: "legacy",
      }),
    );
  }
  claim({
    harmony: { kind: "builtin", key: "title" },
    target: ITEM_NAME_TARGET,
    direction: "create",
    source: "default",
  });
  return { pairings, usedFields, usedTargets };
}

/**
 * Fields and targets a new explicit entry may not use: every one claimed by
 * an explicit entry or a legacy slot. A default's sides stay offered, since
 * an explicit entry is how a tenant replaces it.
 */
export function claimedByConfig(sync: MondayBoardSync | undefined): {
  fields: Set<string>;
  targets: Set<string>;
} {
  const { pairings } = boardPairings(sync);
  const fields = new Set<string>();
  const targets = new Set<string>();
  if (sync?.files_column_id) targets.add(sync.files_column_id);
  for (const p of pairings) {
    if (p.source === "default") continue;
    fields.add(fieldIdentity(p.harmony));
    targets.add(p.target);
  }
  return { fields, targets };
}
